import React, { useState, useEffect, FC, useCallback, memo, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { useTranslation } from 'react-i18next';
import { Alert, Grid, MenuItem, Select, Stack } from '@mui/material';
import { settingType } from '../types';
import {
  crtCalculatedTimeObj,
  dates,
  hours,
  months,
  useFetchJsonFromS3,
  years,
} from '../constants/calculatedTimeList';
import { timeDelta } from '../../../utils/timeRelatedFunctions';
import { useSnackbar } from '../../../components/hooks/useSnackbar';
import { autoZero } from '../../../utils/autoZero';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '1px solid #ddd',
  boxShadow: 24,
  p: 4,
};

type argType = {
  increment: number;
  setIncrement: React.Dispatch<React.SetStateAction<number>>;
  setCalculatedTime: React.Dispatch<React.SetStateAction<Date | null>>;
  calculatedTime: Date | null;
};

const useSettingModal = ({
  increment,
  setIncrement,
  setCalculatedTime,
  calculatedTime,
}: argType) => {
  const [show, setShow] = useState<boolean>(false);
  const toggleModalShow = useCallback(() => {
    setShow(show => !show);
  }, []);
  const closeModal = useCallback(() => {
    setShow(false);
  }, []);

  return {
    toggleModalShow,
    Modal: (
      <SettingModal
        show={show}
        closeModal={closeModal}
        increment={increment}
        setIncrement={setIncrement}
        setCalculatedTime={setCalculatedTime}
        calculatedTime={calculatedTime}
      />
    ),
  };
};

type ModalProps = {
  show: boolean;
  closeModal: () => void;
  increment: number;
  setIncrement: React.Dispatch<React.SetStateAction<number>>;
  setCalculatedTime: React.Dispatch<React.SetStateAction<Date | null>>;
  calculatedTime: Date | null;
};

const SettingModal: FC<ModalProps> = memo(
  ({ show, closeModal, increment, setIncrement, setCalculatedTime, calculatedTime }) => {
    const { t } = useTranslation();
    const { calculatedTimeList } = useFetchJsonFromS3();
    const { CustomSnackbar, setWindowAlert } = useSnackbar();
    const [settings, setSettings] = useState<settingType>({
      increment: increment,
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      date: new Date().getDate(),
      hour: new Date().getHours(),
    });
    const dateTree = crtCalculatedTimeObj(calculatedTimeList);

    useEffect(() => {
      if (!calculatedTime) return;
      setSettings({
        ...settings,
        year: calculatedTime.getFullYear(),
        month: calculatedTime.getMonth() + 1,
        date: calculatedTime.getDate(),
        hour: calculatedTime.getHours(),
      });
    }, [calculatedTime]);
    const handleSaveSettings = () => {
      setIncrement(settings.increment);
      // まずは数字を再度UTCに変換
      const date = new Date(settings.year, settings.month - 1, settings.date, settings.hour);
      const copiedDate = new Date(date.getTime());
      date.setHours(date.getHours() - timeDelta);

      // 数値を2桁の文字列に変換
      const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
      const dayStr = date.getDate().toString().padStart(2, '0');
      const hourStr = date.getHours().toString().padStart(2, '0');
      const formattedDate = `${settings.year}${monthStr + dayStr + hourStr}`;
      const tmpImageId = 'te_flddph_' + formattedDate;
      if (!!calculatedTimeList.includes(tmpImageId)) {
        setCalculatedTime(copiedDate);
        closeModal();
        return;
      }
      setWindowAlert(t('pages.predictionMap.settings.selectedTimeNotExist'), 'warning');
    };

    const errorMessage = useMemo(() => {
      if (!settings.year
        || !settings.month
        || !settings.date
        || (!settings.hour && typeof settings.hour !== 'number')
        || !settings.increment
      ) {
        return '未入力の項目があります。'
      }

      const date = new Date(settings.year, settings.month - 1, settings.date, settings.hour);
      date.setHours(date.getHours() - timeDelta);

      // 数値を2桁の文字列に変換
      const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
      const dayStr = date.getDate().toString().padStart(2, '0');
      const hourStr = date.getHours().toString().padStart(2, '0');
      const formattedDate = `${settings.year}${monthStr + dayStr + hourStr}`;
      const tmpImageId = 'te_flddph_' + formattedDate;

      if (!calculatedTimeList.includes(tmpImageId)) {
        return t('pages.predictionMap.settings.selectedTimeNotExist');
      }

      return null;
    }, [settings, t, calculatedTimeList]);

    return (
      <>
        {' '}
        <Modal
          open={show}
          onClose={closeModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <>
            {CustomSnackbar}

            <Box sx={style}>
              <Typography variant="h6">{t('pages.predictionMap.settings.dataSetting')}</Typography>
              <Stack spacing={1} mt={2} direction="row">
                <Box style={{ marginTop: '4px', width: 'auto' }}>
                  <Typography>{t('pages.predictionMap.settings.calculationTime')}</Typography>
                </Box>
                <Stack spacing={1} direction="row">

                  {/* 起算時刻：年 */}
                  <Select
                    variant="standard"
                    value={settings.year}
                    onChange={e => setSettings({ ...settings, year: Number(e.target.value) })}
                    error={Object.keys(dateTree).filter(year => settings.year === Number(year)).length === 0}
                    sx={{
                      '&.Mui-error': {
                        background: 'rgb(253, 237, 237)',
                      }
                    }}
                  >
                    {years.map(y => (
                      <MenuItem
                        value={y}
                        key={y}
                        disabled={!Object.keys(dateTree).includes(String(y))}
                      >
                        {t('pages.predictionMap.settings.yearOption', { year: y })}
                      </MenuItem>
                    ))}
                  </Select>
                  {/* 起算時刻：月 */}
                  <Select
                    variant="standard"
                    value={settings.month}
                    onChange={e => setSettings({ ...settings, month: Number(e.target.value) })}
                    error={!!dateTree[`${settings.year}`] && Object.keys(dateTree[`${settings.year}`]).filter(month => settings.month === Number(month)).length === 0}
                    sx={{
                      '&.Mui-error': {
                        background: 'rgb(253, 237, 237)',
                      }
                    }}
                  >
                    {months.map(m => (
                      <MenuItem
                        value={m}
                        key={m}
                        disabled={
                          !!dateTree[`${settings.year}`] &&
                          !Object.keys(dateTree[`${settings.year}`]).includes(String(m))
                        }
                      >
                        {t('pages.predictionMap.settings.monthOption', { month: m })}
                      </MenuItem>
                    ))}
                  </Select>
                  {/* 起算時刻：日 */}
                  <Select
                    variant="standard"
                    value={settings.date}
                    onChange={e => setSettings({ ...settings, date: Number(e.target.value) })}
                    error={
                      !!dateTree[`${settings.year}`] &&
                      !!dateTree[`${settings.year}`][`${settings.month}`] &&
                      Object.keys(dateTree[`${settings.year}`][`${settings.month}`]).filter(day => settings.date === Number(day)).length === 0
                    }
                    sx={{
                      '&.Mui-error': {
                        background: 'rgb(253, 237, 237)',
                      }
                    }}
                  >
                    {dates.map(d => (
                      <MenuItem
                        value={d}
                        key={d}
                        disabled={
                          !!dateTree[`${settings.year}`] &&
                          !!dateTree[`${settings.year}`][`${settings.month}`] &&
                          !Object.keys(dateTree[`${settings.year}`][`${settings.month}`]).includes(
                            String(d),
                          )
                        }
                      >
                        {t('pages.predictionMap.settings.dateOption', { date: d })}
                      </MenuItem>
                    ))}
                  </Select>
                  {/* 起算時刻：時 */}
                  <Select
                    variant="standard"
                    value={settings.hour}
                    onChange={e => setSettings({ ...settings, hour: Number(e.target.value) })}
                    error={
                      !!dateTree[`${settings.year}`] &&
                      !!dateTree[`${settings.year}`][`${settings.month}`] &&
                      !!dateTree[`${settings.year}`][`${settings.month}`][
                        `${settings.date}`
                      ] &&
                      dateTree[`${settings.year}`][`${settings.month}`][
                        `${settings.date}`
                      ].filter(hour => settings.hour === hour).length === 0
                    }
                    sx={{
                      '&.Mui-error': {
                        background: 'rgb(253, 237, 237)',
                      }
                    }}
                  >
                    {hours.map(h => {
                      return (
                        <MenuItem
                          value={h}
                          key={h}
                          disabled={
                            !!dateTree[`${settings.year}`] &&
                            !!dateTree[`${settings.year}`][`${settings.month}`] &&
                            !!dateTree[`${settings.year}`][`${settings.month}`][
                              `${settings.date}`
                            ] &&
                            !dateTree[`${settings.year}`][`${settings.month}`][
                              `${settings.date}`
                            ].includes(h)
                          }
                        >
                          {t('pages.predictionMap.settings.hourOption', { hour: h })}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Stack>
              </Stack>
              
              <Stack spacing={1} mt={2} direction="row">
                <Box style={{ marginTop: '4px', width: '94px' }}>
                  <Typography style={{ marginTop: '3px' }}>
                    {t('pages.predictionMap.settings.dataTimeIncrement')}
                  </Typography>
                </Box>
                <Select
                  labelId="demo-simple-select-label"
                  value={settings.increment}
                  onChange={e => setSettings({ ...settings, increment: Number(e.target.value) })}
                  label={t('pages.predictionMap.settings.dataTimeIncrement')}
                  variant="standard"
                >
                  <MenuItem value={6}>
                    {t('pages.predictionMap.settings.timeIntervalOption', { interval: 6 })}
                  </MenuItem>
                  <MenuItem value={3}>
                    {t('pages.predictionMap.settings.timeIntervalOption', { interval: 3 })}
                  </MenuItem>
                  <MenuItem value={1}>
                    {t('pages.predictionMap.settings.timeIntervalOption', { interval: 1 })}
                  </MenuItem>
                </Select>
              </Stack>
              
              { errorMessage && <Alert sx={{ mt: 2 }} severity='error'>{errorMessage}</Alert> }

              {/* ここから下はボタン */}
              <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  style={{
                    color: '#aaa',
                    border: '0.2px solid #aaa',
                  }}
                  size="small"
                  onClick={closeModal}
                >
                  {t('pages.predictionMap.settings.cancel')}
                </Button>
                <Button variant="contained" size="small" onClick={handleSaveSettings} disabled={!!errorMessage}>
                  {t('pages.predictionMap.settings.saveSettings')}
                </Button>
              </Stack>
            </Box>
          </>
        </Modal>
      </>
    );
  },
);

export default useSettingModal;
